<template>
  <div
    role="button"
    :class="[$options.name, statusClassList, { 'tw:!cursor-pointer': !showCloseIcon }]"
    :data-e-ga-id="uniqueId"
    :aria-pressed="active"
    :aria-disabled="disabled"
    @click="setActive"
  >
    <slot name="prefix" />

    <span class="tw:select-none" v-text="text"></span>

    <slot name="suffix" />

    <font-awesome-icon
      v-if="active && showCloseIcon"
      :class="[$options.name + '__icon']"
      class="tw:!ml-1"
      icon="fa-solid fa-xmark"
      @click.stop="setInactive"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from "vue";
import { gtagUniversalSend } from "vue-app/plugins/edroneGtag";

export default defineComponent({
  name: "EdroneTag",
  props: {
    /**
     * Name of the Tag
     */
    text: {
      type: String,
      default: "Tag name",
    },
    type: {
      type: String as PropType<"default" | "yellow">,
      default: "default",
    },
    size: {
      type: String as PropType<"small" | "big">,
      default: "big",
    },
    active: {
      type: Boolean,
      default: false,
    },
    /**
     * Whether the Tag should be rendered as disabled
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    uniqueId: {
      type: String,
      default: "",
    },
    showCloseIcon: {
      type: Boolean,
      default: true,
    },
  },
  emits: [
    /**
     * Triggers when you click on a tag when tag is inactive or close icon when tag is active
     * @type {string}
     */
    "click",
  ],
  setup(props, context) {
    const statusClassList = computed(() => {
      return {
        "EdroneTag--default": props.type === "default",
        "EdroneTag--yellow": props.type === "yellow",
        "EdroneTag--active": props.active,
        "EdroneTag--big": props.size === "big",
        "EdroneTag--small": props.size === "small",
        "EdroneTag--disabled": props.disabled,
      };
    });

    function setActive() {
      if (!props.disabled && (!props.active || !props.showCloseIcon)) {
        context.emit("click", props.text);
        gtagUniversalSend({ component_id: props.uniqueId });
      }
    }

    function setInactive() {
      context.emit("click", props.text);
    }

    return {
      statusClassList,
      setActive,
      setInactive,
    };
  },
});
</script>

<style scoped>
@reference "vue-app/assets/scss/main.css";

.EdroneTag {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: fit-content;
  height: fit-content;
  text-align: center;
  cursor: pointer;
  margin: 0;
}

.EdroneTag {
  @apply tw:text-sm tw:leading-5 tw:rounded-sm tw:py-1.5 tw:px-2 tw:gap-1;
}

.EdroneTag span {
  color: inherit;
}

.EdroneTag__icon {
  margin-left: 2px;
  padding: 2px 4px;
  cursor: pointer;
}

@layer utilities {
  .EdroneTag__icon {
    @apply tw:text-black tw:text-xs;
  }
}

.EdroneTag--small {
  @apply tw:text-xs tw:leading-4 tw:py-1 tw:px-2;
}

.EdroneTag--default {
  @apply tw:text-black tw:bg-white tw:border tw:border-default-200;
}

.EdroneTag--default:hover {
  @apply tw:border tw:border-blue-500;
}

.EdroneTag--default .EdroneTag__icon:hover {
  border-radius: 3px;
  @apply tw:bg-blue-100;
}

.EdroneTag--default.EdroneTag--active {
  @apply tw:text-black tw:bg-blue-100 tw:border tw:border-blue-500;
}

.EdroneTag--default.EdroneTag--active:hover {
  @apply tw:text-blue-600;
}

.EdroneTag--default.EdroneTag--active:hover .EdroneTag__icon {
  @apply tw:!text-blue-600;
}

.EdroneTag--default.EdroneTag--active .EdroneTag__icon:hover {
  border-radius: 3px;
  @apply tw:bg-blue-200;
}

.EdroneTag--default.EdroneTag--disabled {
  pointer-events: none !important;
  user-select: none !important;
  @apply tw:!text-default-500 tw:!bg-default-000 tw:!border-default-000 tw:!border;
}

.EdroneTag--default.EdroneTag--disabled .EdroneTag__icon {
  @apply tw:!text-default-500;
}

.EdroneTag--yellow {
  @apply tw:text-black tw:bg-branding-100 tw:border tw:border-branding-600;
}

.EdroneTag--yellow:hover {
  @apply tw:border tw:border-branding-700;
}

.EdroneTag--yellow .EdroneTag__icon:hover {
  border-radius: 3px;
  @apply tw:bg-branding-600;
}

.EdroneTag--yellow.EdroneTag--active {
  @apply tw:text-black tw:bg-branding-500 tw:border tw:border-branding-600;
}

.EdroneTag--yellow.EdroneTag--active:hover {
  @apply tw:border tw:border-branding-700;
}

.EdroneTag--yellow.EdroneTag--active .EdroneTag__icon:hover {
  border-radius: 3px;
  @apply tw:bg-branding-600;
}

.EdroneTag--yellow.EdroneTag--disabled {
  pointer-events: none !important;
  user-select: none !important;
  @apply tw:!text-default-500 tw:!bg-default-000 tw:!border-default-000 tw:!border;
}

.EdroneTag--yellow.EdroneTag--disabled .EdroneTag__icon {
  @apply tw:!text-default-500;
}

.EdroneTag--active {
  cursor: default;
}
</style>
